import { Clinic, ClinicalInfo } from "@app/provider/types";
import type { DrawCoordinator } from "@app/provider/types";
import { SimpleOrderWorkflowState } from "@app/provider/order";
import { GenderOption } from "../testResult";

export enum ManagedBy {
  CLINIC = "CLINIC",
  NATERA = "NATERA",
}

export enum KitAppStatus {
  SHIPPED = "SHIPPED",
}

export enum ProsperaOrganType {
  HEART = "HEART",
  KIDNEY = "KIDNEY",
  LIVER = "LIVER",
  LUNG = "LUNG",
  SPK = "SPK",
  OTHER = "OTHER",
}

export enum SampleType {
  BLOOD = "BLOOD",
  FIBROBLAST = "FIBROBLAST",
  SALIVA = "SALIVA",
  TISSUE = "TISSUE",
}

export enum TestCardStatus {
  TEST_ORDERED = "TEST_ORDERED",
  UPCOMING_TEST = "UPCOMING_TEST",
  KIT_SHIPPED = "KIT_SHIPPED",
  APPT_REQUESTED = "APPT_REQUESTED",
  APPT_CONFIRMED = "APPT_CONFIRMED",
  SAMPLE_RECEIVED = "SAMPLE_RECEIVED",
  SAMPLES_RECEIVED = "SAMPLES_RECEIVED",
  PROCESSING = "PROCESSING",
  RESULT_READY = "RESULT_READY",
  TEST_COMPLETE = "TEST_COMPLETE",
  CANCELLED_TEST = "CANCELLED_TEST",
  EXPIRED_TEST = "EXPIRED_TEST",
  REDRAW_NEEDED = "REDRAW_NEEDED",
  NEW_TISSUE_NEEDED = "NEW_TISSUE_NEEDED",
}

export enum TestStatus {
  NOT_ACCESSED = "NOT_ACCESSED",
  VIEWED = "VIEWED",
  VIEW_WITH_PHYSICIAN = "VIEW_WITH_PHYSICIAN",
}

export enum TestType {
  ALTERA = "ALTERA",
  ANORA = "ANORA",
  EMPOWER = "EMPOWER",
  HORIZON = "HORIZON",
  PANORAMA = "PANORAMA",
  PROSPERA = "PROSPERA",
  RENASIGHT = "RENASIGHT",
  SIGNATERA = "SIGNATERA",
  SPECTRUM = "SPECTRUM",
  VISTARA = "VISTARA",
  SINGLE_GENE_NIPT = "SINGLE_GENE_NIPT",
  LATITUDE = "LATITUDE",
  DPYD = "DPYD",
}

export enum NativeTestType {
  CARRIER_SCREENING_3 = "CARRIER_SCREENING_3",
  CARRIER_SCREENING_X = "CARRIER_SCREENING_X",
  CARRIER_SCREENING_SOPL = "CARRIER_SCREENING_SOPL",
}

export const ONCOLOGY_TEST_TYPES = [
  TestType.ALTERA,
  TestType.EMPOWER,
  TestType.SIGNATERA,
  TestType.LATITUDE,
  TestType.DPYD,
];

export enum ComboTestType {
  HORIZON_PANORAMA_COMBO = "HORIZON_PANORAMA_COMBO",
  PANORAMA_VISTARA_COMBO = "PANORAMA_VISTARA_COMBO",
  HORIZON_VISTARA_COMBO = "HORIZON_VISTARA_COMBO",
  COMBO_VISTARA = "COMBO_VISTARA",
}

// Known SOS/MPX job types used in UPP
export enum KnownMobilePhlebotomyJobType {
  PROSPERA = "PROSPERA",
  PROSPERA_HEART = "PROSPERA_HEART",
  PROSPERA_LUNG = "PROSPERA_LUNG",
  RENASIGHT = "RENASIGHT",
  HORIZON = "HORIZON",
  PANORAMA = "PANORAMA",
  EMPOWER = "EMPOWER",
  SIGNATERA = "SIGNATERA",
  ALTERA = "ALTERA",
  VISTARA = "VISTARA",
  SPECTRUM = "SPECTRUM",
  ANORA = "ANORA",
  HORIZON_PANORAMA_COMBO = "HORIZON_PANORAMA_COMBO",
  PANORAMA_VISTARA_COMBO = "PANORAMA_VISTARA_COMBO",
  HORIZON_VISTARA_COMBO = "HORIZON_VISTARA_COMBO",
  COMBO_VISTARA = "COMBO_VISTARA",
  SIGNATERA_FOLLOW_UP = "SIGNATERA_FOLLOW_UP",
  SIGNATERA_ALTERA_COMBO = "SIGNATERA_ALTERA_COMBO",
  LATITUDE = "LATITUDE",
  SINGLE_GENE_NIPT = "SINGLE_GENE_NIPT",
}

// Flexible type that accepts any string (for unknown values from SOS/MPX)
export type MobilePhlebotomyJobType = KnownMobilePhlebotomyJobType | string;

export enum PrimaryAction {
  SCHEDULE_A_DRAW = "SCHEDULE_A_DRAW",
  TRACK_KIT_STATUS = "TRACK_KIT_STATUS",
  APPOINTMENT_DETAIL = "APPOINTMENT_DETAIL",
  VIEW_YOUR_RESULTS = "VIEW_YOUR_RESULTS",
  COMPLETE_ORDER = "COMPLETE_ORDER",
  VIEW_TEST_DETAILS = "VIEW_TEST_DETAILS",
  VIEW_RESULT_PAGE = "VIEW_RESULT_PAGE",
}

export enum SecondaryActionCardType {
  COMPASSIONATE_CARE = "COMPASSIONATE_CARE",
  SALIVA = "SALIVA",
  EMPOWER = "EMPOWER",
  HORIZON = "HORIZON",
  PANORAMA = "PANORAMA",
  PROSPERA_KIDNEY = "PROSPERA_KIDNEY",
  PROSPERA_SPK = "PROSPERA_SPK",
  PROSPERA_HEART = "PROSPERA_HEART",
  PROSPERA_LIVER = "PROSPERA_LIVER",
  PROSPERA_LUNG = "PROSPERA_LUNG",
  PROSPERA_OTHER = "PROSPERA_OTHER",
  RENASIGHT_KIDNEY = "RENASIGHT_KIDNEY",
  VISTARA = "VISTARA",
  GENETIC_TESTING = "GENETIC_TESTING",
  WOMENS_HEALTH_PRICING_BILLING = "WOMENS_HEALTH_PRICING_BILLING",
  ONCOLOGY_PRICING_BILLING = "ONCOLOGY_PRICING_BILLING",
  PANORAMA_HORIZON = "PANORAMA_HORIZON",
  ALTERA = "ALTERA",
  SIGNATERA = "SIGNATERA",
}

export enum SecondaryActionCardTypeForHeapEvent {
  COMPCARE_INFO = "compcare_info",
  BILLING_FAQ_WH = "billing_FAQ_WH",
  BILLING_FAQ_ONC = "billing_FAQ_ONC",
  EMPOWER_INFO = "empower_info",
  HORIZON_INFO = "horizon_info",
  VISTARA_INFO = "vistara_info",
  PANORAMA_INFO = "panorama_info",
  RENASIGHT_INFO = "renasight_info",
  PROSPERA_INFO = "prospera_info",
  SALIVA_COLLECTION_INFO = "saliva_collection_info",
  MULTITEST_INFO_WH = "multitest_info_WH",
  HORIZON_PANORAMA_INFO = "horizon_panorama_info",
  ALTERA_INFO = "altera_info",
  SIGNATERA_INFO = "signatera_info",
}

export interface TestCard {
  uid: string;
  orderUidPublic: string;
  testType: TestType;
  status: TestCardStatus;
  nextStatus?: TestCardStatus;
  managedBy?: ManagedBy;
  drawCoordinator?: DrawCoordinator;
  sampleCollectionDate?: Date;
  sampleType?: SampleType;
  sampleReceivedDate?: Date;
  PTPOption: boolean;
  viewStatus?: TestStatus;
  organType?: ProsperaOrganType;
  resultExpectedByDate?: Date;
  createdAt: Date;
  accessedResultAt?: Date;
  kitAppStatus?: KitAppStatus;
  trackingNumber?: string;
  kitShippedDate?: string;
  reportGender?: boolean;
  caPnsSupplemental?: boolean;
  resultCode?: string;
  fetalSex?: GenderOption;
  historicalMtmValues?: HistoricalMtmValue[];
  currentMtmValue?: number;
  mtmDelta?: number;
  resultReleasedAt: Date | null;
  latestResultDocumentUid?: string;
  cancelledAt?: Date;
  isProReach?: boolean;
  clinic?: Clinic;
  isComboOrder?: boolean;
  isNevaEnabled?: boolean;
  isPippaEnabled?: boolean;
  isOrderInSignedOrdersTable?: boolean;
  simpleOrderWorkflowState?: SimpleOrderWorkflowState;
  isSignateraSubsequent?: boolean;
  isOncologyOrder?: boolean;
  isEmpowerStatPanelIncluded?: boolean;
  isLatestReleasedResultStat?: boolean;
  isStatResultAlreadyViewed?: boolean;
  isTfMrdActive?: boolean;
  tfMrdOrderingOption?: TfmrdOption;
  isReflexed?: boolean;
  patient?: PatientInfo;
  nativeTestType?: NativeTestType;
  isPatientResultReleaseDelayed?: boolean;
  patientResultAvailableAt?: Date | null;

  // POC: Backend-computed display values (rules-based cards)
  // If present, frontend should use these instead of recomputing
  title?: string;
  description?: string;
  statusText?: string;
  primaryAction?: PrimaryAction;
  i18nKeys?: {
    title?: string;
    statusText?: string;
    description?: string;
    primaryAction?: string;
    bottomLine?: string;
    params?: {
      date?: string;
      estimatedResultsDate?: string;
      sampleCollectionDate?: string;
      sampleReceivedDate?: string;
      createdAt?: string;
      resultsDelayedUntilDate?: string;
      resultExpectedByDate?: string;
    };
  };
  clinicalInfo?: ClinicalInfo;
}

export interface ParsedTestCard {
  uid: string;
  orderUidPublic: string;
  testType: TestType;
  createdAt: Date;
  accessedResultAt?: Date;
  status: TestCardStatus;
  nextStatus?: TestCardStatus;
  description?: string;
  statusText?: string; // Translated status text (rules-based cards)
  primaryAction?: PrimaryAction;
  // Pass through i18nKeys for components that need them
  i18nKeys?: {
    title?: string;
    statusText?: string;
    description?: string;
    primaryAction?: string;
    nextStatus?: string;
    bottomLine?: string;
    params?: {
      date?: string;
      estimatedResultsDate?: string;
      sampleCollectionDate?: string;
      sampleReceivedDate?: string;
      createdAt?: string;
      resultsDelayedUntilDate?: string;
      resultExpectedByDate?: string;
    };
  };
  organType?: ProsperaOrganType;
  managedBy?: ManagedBy;
  drawCoordinator?: DrawCoordinator;
  sampleType?: SampleType;
  sampleReceivedDate?: Date;
  trackingNumber?: string;
  reportGender?: boolean;
  caPnsSupplemental?: boolean;
  resultCode?: string;
  fetalSex?: GenderOption;
  resultReleasedAt: Date | null;
  latestResultDocumentUid?: string;
  cancelledAt?: Date;
  isProReach?: boolean;
  clinic?: Clinic;
  isComboOrder?: boolean;
  isNevaEnabled?: boolean;
  isOrderInSignedOrdersTable?: boolean;
  simpleOrderWorkflowState?: SimpleOrderWorkflowState;
  isSignateraSubsequent?: boolean;
  isEmpowerStatPanelIncluded?: boolean;
  isLatestReleasedResultStat?: boolean;
  isStatResultAlreadyViewed?: boolean;
  isTfMrdActive?: boolean;
  tfMrdOrderingOption?: TfmrdOption;
  isReflexed?: boolean;
  clinicalInfo?: ClinicalInfo;
  isPatientResultReleaseDelayed?: boolean;
  patientResultAvailableAt?: Date | null;
}

export interface SecondaryCard {
  type: SecondaryActionCardType;
  title?: string;
  description?: string;
  note?: string;
  limsClinicId?: number;
}

export interface EmpowerRiskAlertInfo {
  isEligible: boolean;
  clinicLimsId?: number;
}

/**
 * Result codes used for conditional routing logic (e.g. acuity appointment
 * type selection).
 */
export enum TestResultCode {
  SHORT_DRAW = "SHORT_DRAW",
  TEST_CANCELLED = "TEST_CANCELLED",
  MISSING_REQUIRED_INFORMATION = "MISSING_REQUIRED_INFORMATION",
  MISSING_BILLING = "MISSING_BILLING",
  MISSING_BILLING_CONTACT = "MISSING_BILLING_CONTACT",
  PATIENT_NOT_PREGNANT = "PATIENT_NOT_PREGNANT",
  DONOR_MULTIPLE_GESTATION = "DONOR_MULTIPLE_GESTATION",
  NO_SAMPLE = "NO_SAMPLE",
  MALE_PATIENT = "MALE_PATIENT",
  HIGH_MULTIPLE_GESTATION = "HIGH_MULTIPLE_GESTATION",
  VANISHED_TWIN = "VANISHED_TWIN",
  DUPLICATE_TEST = "DUPLICATE_TEST",
  EXPIRED_MI = "EXPIRED_MI",
  SAMPLE_DAMAGED = "SAMPLE_DAMAGED",
  SAMPLE_COLLECTION_DATE_TOO_OLD = "SAMPLE_COLLECTION_DATE_TOO_OLD",
  LOW_GESTATIONAL_AGE = "LOW_GESTATIONAL_AGE",
  WRONG_COLLECTION_TUBE = "WRONG_COLLECTION_TUBE",
  ID_DISCREPANCY = "ID_DISCREPANCY",
  INSUFFICIENT_PLASMA_VOLUME = "INSUFFICIENT_PLASMA_VOLUME",
  INSUFFICIENT_DNA = "INSUFFICIENT_DNA",
  INSUFFICIENT_DNA_NOR = "INSUFFICIENT_DNA_NOR",
  SAMPLE_CONTAMINATED = "SAMPLE_CONTAMINATED",
  LOW_FETAL_FRACTION = "LOW_FETAL_FRACTION",
  LOW_FETAL_FRACTION_INTERMEDIATE = "LOW_FETAL_FRACTION_INTERMEDIATE",
  SUSPECTED_MATERNAL_MOSAICISM = "SUSPECTED_MATERNAL_MOSAICISM",
  SUSPECTED_FETAL_MOSAICISM = "SUSPECTED_FETAL_MOSAICISM",
  UNMATCHING_MATERNAL_ALLELES = "UNMATCHING_MATERNAL_ALLELES",
  SUSPECTED_NON_DONOR_OR_SURROGATE_PREGNANCY = "SUSPECTED_NON_DONOR_OR_SURROGATE_PREGNANCY",
  SUSPECTED_MULTIPLE_GESTATION_OR_TRIPLOIDY = "SUSPECTED_MULTIPLE_GESTATION_OR_TRIPLOIDY",
  SUSPECTED_MATERNAL_TRIPLOIDY = "SUSPECTED_MATERNAL_TRIPLOIDY",
  MATERNAL_HAPLOBLOCKS = "MATERNAL_HAPLOBLOCKS",
  FETAL_HAPLOBLOCKS = "FETAL_HAPLOBLOCKS",
  MULTIPLE_SEQUENCING_FAILURES = "MULTIPLE_SEQUENCING_FAILURES",
  UNEXPLAINED_BAD_MODEL_FIT = "UNEXPLAINED_BAD_MODEL_FIT",
  STATS_NO_CALL_OTHER = "STATS_NO_CALL_OTHER",
  TRACER_FAILURE = "TRACER_FAILURE",
  LAB_ERROR = "LAB_ERROR",
  TNP_OTHER = "TNP_OTHER",
  UNKNOWN = "UNKNOWN",
  LOW_RISK_RESULT = "LOW_RISK_RESULT",
  HIGH_RISK_RESULT = "HIGH_RISK_RESULT",
  NEGATIVE = "NEGATIVE",
  CARRIER = "CARRIER",
  HIGH_RISK_RESULT_FFBR = "HIGH_RISK_RESULT_FFBR",
  SINGLE_CHROM_HAPLOBLOCK_MATERNAL = "SINGLE_CHROM_HAPLOBLOCK_MATERNAL",
  SINGLE_CHROM_HAPLOBLOCK_FETAL = "SINGLE_CHROM_HAPLOBLOCK_FETAL",
  SINGLE_CHROM_MATERNAL_DEL_DUP = "SINGLE_CHROM_MATERNAL_DEL_DUP",
  SINGLE_CHROM_MATERNAL_ANEUPLOIDY = "SINGLE_CHROM_MATERNAL_ANEUPLOIDY",
  SINGLE_CHROM_LOW_CONF = "SINGLE_CHROM_LOW_CONF",
  STATS_NO_CALL_OTHER_SINGLE_CHROM = "STATS_NO_CALL_OTHER_SINGLE_CHROM",
  ABNORMAL_RESULT_SINGLE_CHROM = "ABNORMAL_RESULT_SINGLE_CHROM",
  HIGH_RISK_WITH_SINGLE_CHROM_NO_CALL = "HIGH_RISK_WITH_SINGLE_CHROM_NO_CALL",
  SUSPECTED_XXY = "SUSPECTED_XXY",
  SUSPECTED_XXX = "SUSPECTED_XXX",
  SUSPECTED_XYY = "SUSPECTED_XYY",
  SEVERE_HEMOLYSIS = "SEVERE_HEMOLYSIS",
  UNINTERPRETABLE_RESULT_NO_REDRAW = "UNINTERPRETABLE_RESULT_NO_REDRAW",
  CA_SUPP_DI_TWINS = "CA_SUPP_DI_TWINS",
  CA_SUPP_DONOR = "CA_SUPP_DONOR",
}

export enum TfmrdOption {
  NONE = "NONE",
  REFLEX = "REFLEX",
}

export type HistoricalMtmValue = {
  date?: string;
  mtm?: number;
  resultCode?: string;
};

export type PatientInfo = {
  firstName?: string;
};
